import { bindable, autoinject, observable, computedFrom } from 'aurelia-framework';
import { Municipality } from '../../municipalities/municipality-service';
import {
	ReportTemplateColumnType,
	ComparisonType,
    IReportTemplateColumn
} from '../models/report-template';
import { TabService } from '../../../components/tabs/tab-service';
import { IScopeAM } from '../../scopes/scope-service';
import { OrganizationalNodeModel } from '../../customers/organizational-units/organizational-unit-service';
import { IReportHeaderCellAM } from '../report-api';
import { noop } from 'lodash';
import { v4 as uuid } from 'uuid';
import { OrganizationalVM } from '../containers/report-column-add';

@autoinject
export class ReportColumnAddForm {
	@bindable visible: boolean;
	@bindable scopes: IScopeAM[];
	@bindable municipalities: Municipality[];
	@bindable organizationalUnits: OrganizationalVM[];
	@bindable columns: IReportHeaderCellAM[];
	@bindable comparisonTypes: ComparisonType[];
	@bindable direction: string;
	@bindable loading = false;
	@bindable addColumn: (params: { column: IReportTemplateColumn }) => void;
	@bindable hideComponent: () => void;
	@bindable changeScope: (params: { scope: IScopeAM }) => Promise<void>;
	@bindable search: (params: { searchString: string }) => number;
	@bindable disableMunicipalitySelection: boolean;

	@observable selectedScope: IScopeAM;

	columnType = ReportTemplateColumnType.Data;
	selectedMunicipality: Municipality;
	selectedOrganizationalUnit: OrganizationalVM;
	selectedColumn1: IReportHeaderCellAM;
	selectedColumn2: IReportHeaderCellAM;
	selectedComparisonType: ComparisonType;
	customTitle: string;

	constructor(private tabService: TabService) {
	}

	@computedFrom('columns')
	get dataColumns() {
		return this.columns.filter(col => col.type === ReportTemplateColumnType.Data);
	}

	@computedFrom('selectedColumn1', 'selectedColumn2', 'selectedComparisonType', 'selectedScope', 'columnType')
	get columnCreationEnabled() : boolean {
		if (this.columnType === ReportTemplateColumnType.Comparison) {
			return !!this.selectedColumn1 && !!this.selectedColumn2 && !!this.selectedComparisonType;
		}
		else if (this.columnType === ReportTemplateColumnType.Data) {
			return !!this.selectedScope;
		}
		return false;
	}

	submit() {
		const createColumn = (type: ReportTemplateColumnType) => {
			const createDataColumn = () => {
				let municipalityCode: string | number;
				if (this.selectedMunicipality == null) {
					municipalityCode = null;
				} else if (this.selectedMunicipality.type === 'ExternalMunicipalities') {
					municipalityCode = 'External';
				} else {
					municipalityCode = this.selectedMunicipality.code;
				}

				const [organizationalUnit, costCenter] = this.selectedOrganizationalUnit != null
					? ((node: OrganizationalNodeModel) => {
						switch (node.nodeType) {
							case 'OrganizationalUnit':
								return [node.identifier, null];
							case 'CostCenter':
								return [null, node.identifier];
							default:
								throw new Error(`Node type ${node.nodeType} is invalid.`);
						}
					})(this.selectedOrganizationalUnit.unit)
					: [null, null];

				return {
					id: uuid(),
					type: ReportTemplateColumnType.Data,
					scope: this.selectedScope.id,
					municipality: municipalityCode,
					organizationalUnit,
					costCenter,
					title: this.customTitle || null
				};
			};

			const createComparisonColumn = () => {
				return {
					id: uuid(),
					type: ReportTemplateColumnType.Comparison,
					columnIds: [
						this.selectedColumn1.columnId,
						this.selectedColumn2.columnId
					],
					comparisonType: this.selectedComparisonType
				};
			}

			switch (type) {
				case ReportTemplateColumnType.Data:
					return createDataColumn();
				case ReportTemplateColumnType.Comparison:
					return createComparisonColumn();
				default:
					throw new Error(`Invalid column type ${type}`);
			}
		};

		const column = createColumn(this.columnType);

		this.addColumn({ column });

		this.hide();
	}

	onTabChange(tabId: string) {
		switch (tabId) {
			case 'data-column':
				this.columnType = ReportTemplateColumnType.Data;
				break;
			case 'comparison-column':
				this.columnType = ReportTemplateColumnType.Comparison;
				break;
			default:
				throw new Error(`Unsupported tab ID ${tabId}`);
		}
	}

	hide() {
		this.visible = false;
		this.hideComponent();
	}

	visibleChanged(newValue) {
		if (!newValue) {
			this.reset();
		}
	}

	private reset() {
		this.tabService.select('report-column-add', 'data-column');
		this.columnType = ReportTemplateColumnType.Data;
		this.selectedScope = null;
		this.selectedMunicipality = null;
		this.selectedOrganizationalUnit = null;
		this.selectedColumn1 = null;
		this.selectedColumn2 = null;
		this.selectedComparisonType = null;
		this.customTitle = null;
	}

	async selectedScopeChanged(newValue: IScopeAM) {
		await (this.changeScope || noop)({ scope: newValue });
	}

	changeOrganizationUnit(model: OrganizationalVM) {
		this.selectedOrganizationalUnit = model;
	}

	onSearch(searchString: string) {
		return this.search({ searchString });
	}
}
